import { ChatLogger, chatLoggerFactory } from '@/components/chat/ChatLogger'
import { GroupChannelType } from '@/interfaces/chat'
import SendbirdChat from '@sendbird/chat'
import {
  GroupChannel,
  GroupChannelCollection,
  GroupChannelEventContext,
  GroupChannelFilter,
  GroupChannelListOrder,
  GroupChannelModule,
} from '@sendbird/chat/groupChannel'

export class ChatChannelsCollection {
  private channels: GroupChannel[] = []
  private channelType: GroupChannelType
  private collection: GroupChannelCollection
  private logger: ChatLogger
  private sendbirdSdk: SendbirdChat & { groupChannel: GroupChannelModule }
  private subscribers = new Set<() => void>()

  constructor(
    sdk: SendbirdChat & { groupChannel: GroupChannelModule },
    channelType: GroupChannelType
  ) {
    this.sendbirdSdk = sdk
    this.channelType = channelType
    this.logger = chatLoggerFactory.createLogger('ChatChannelsCollection')
    this.initializeCollection()
  }

  subscribe = (subscriber) => {
    this.subscribers.add(subscriber)
    return () => {
      this.subscribers.delete(subscriber)
    }
  }

  getSnapshot = () => {
    return this.channels
  }

  notifySubscribers = () => {
    this.subscribers.forEach((subscriber) => subscriber())
  }

  addChannel = (channel: GroupChannel) => {
    this.logger.debug('addChannel called', GroupChannel)
    const channelIndex = this.channels.findIndex((c) => c.url === channel.url)
    if (channelIndex === -1) {
      this.channels = [channel, ...this.channels]
    } else {
      this.channels.splice(channelIndex, 1)
      this.channels = [channel, ...this.channels]
    }
    this.notifySubscribers()
  }

  listChannels = async (nicknameFilter?: string) => {
    try {
      this.initializeCollection(nicknameFilter)
      if (!this.collection.hasMore) return
      const fetchedChannels = await this.collection.loadMore()
      this.channels = this.removeConnectionsSentChannels(fetchedChannels)
      this.notifySubscribers()

      // TODO: @ilbum fast-follow the query for online users
      /*
      const channelsToFilter = this.channels
      console.log(`channelsToFilter`)
      console.log(channelsToFilter)
      const userIdsFilterToQuery = new Set() // Use a Set to efficiently store unique values
      channelsToFilter.forEach((channel) => {
        channel.members.forEach((member) => {
          if (member?.userId) {
            userIdsFilterToQuery.add(member.userId) // Add unique userIds to the Set
          }
        })
      })
      const uniqueUserIdsArray = Array.from(userIdsFilterToQuery) as string[]
      console.log(`uniqueUserIdsArray`)
      console.log(uniqueUserIdsArray)
      const queryParams: ApplicationUserListQueryParams = {
        userIdsFilter: uniqueUserIdsArray,
      }
      const query: ApplicationUserListQuery =
        this.sendbirdSdk.createApplicationUserListQuery(queryParams)
      console.log(`await query.next()`)
      console.log(await query.next())
      */
    } catch (e) {
      this.logger.error('Sendbird list channels error', e)
    }
  }

  loadMoreChannels = async () => {
    try {
      if (!this.collection || !this.collection.hasMore) return
      const channels = await this.collection.loadMore()
      const parsedChannels = this.removeConnectionsSentChannels(channels)
      this.channels = [...this.channels, ...parsedChannels]
      this.notifySubscribers()
    } catch (e) {
      this.logger.error('Sendbird load more channels error', e)
    }
  }

  private initializeCollection = (nicknameFilter?: string) => {
    if (
      this.collection &&
      this.collection.filter.nicknameContainsFilter === nicknameFilter
    ) {
      return
    }
    if (this.collection) {
      this.collection.dispose()
    }
    const channelFilter = new GroupChannelFilter()
    channelFilter.includeEmpty = false

    // Leave this for Bot conversations
    if (this.channelType === GroupChannelType.bot) {
      channelFilter.includeEmpty = true
    }

    channelFilter.customTypesFilter = [this.channelType]
    if (nicknameFilter) {
      channelFilter.nicknameContainsFilter = nicknameFilter
    }
    this.collection =
      this.sendbirdSdk.groupChannel.createGroupChannelCollection({
        filter: channelFilter,
        limit: 50,
        order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
      })

    this.collection.setGroupChannelCollectionHandler({
      onChannelsAdded: this.onChannelsAdded,
      onChannelsUpdated: this.onChannelsUpdated,
      onChannelsDeleted: this.onChannelsDeleted,
    })
  }

  private onChannelsAdded = (
    context: GroupChannelEventContext,
    channels: GroupChannel[]
  ) => {
    this.logger.debug('onChannelsAdded called', channels)
    const existingChannelUrls = []
    const newChannels = channels.filter((channel) => {
      return !existingChannelUrls.includes(channel.url)
    })
    this.channels = [...newChannels, ...this.channels].sort((a, b) => {
      if (!b.lastMessage?.createdAt) return 1
      if (!a.lastMessage?.createdAt) return -1
      return b.lastMessage?.createdAt - a.lastMessage?.createdAt
    })
    this.notifySubscribers()
  }

  private onChannelsUpdated = (
    context: GroupChannelEventContext,
    channels: GroupChannel[]
  ) => {
    this.logger.debug('onChannelsUpdated called', channels)
    this.channels = this.channels.sort((a, b) => {
      if (!b.lastMessage?.createdAt) return 1
      if (!a.lastMessage?.createdAt) return -1
      return b.lastMessage?.createdAt - a.lastMessage?.createdAt
    })
    this.notifySubscribers()
  }

  private onChannelsDeleted = async (
    context: GroupChannelEventContext,
    channelUrls: string[]
  ) => {
    this.logger.debug('onChannelsDeleted called')
    this.channels = this.channels.filter((ch) => !channelUrls.includes(ch.url))
    if (this.channels.length < 50 && this.collection.hasMore) {
      await this.loadMoreChannels()
    }
    this.notifySubscribers()
  }

  /**
   * Filter the connection-request type channels
   * so that we don't show connections requested by the current user
   */
  private removeConnectionsSentChannels = (channels: GroupChannel[]) => {
    if (this.channelType === GroupChannelType.connection) return channels
    return channels.filter((channel) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const lastMessageSenderUserId = channel?.lastMessage?.sender?.userId
      return lastMessageSenderUserId !== this.sendbirdSdk?.currentUser?.userId
    })
  }
}
